import { useGetDwcDiagnosticAccess, useRunDwcDiagnostic, getGetDwcDiagnosticAccessQueryKey } from '@workspace/api-client-react';
import { Download, Loader2, ShieldCheck } from 'lucide-react';

export function DwcDiagnosticPanel({ request, scope, formatDate }: {
  request: { headers: Record<string, string> }; scope: string; formatDate: (iso: string) => string;
}) {
  const access = useGetDwcDiagnosticAccess({
    request,
    query: { queryKey: [...getGetDwcDiagnosticAccessQueryKey(), scope], retry: false, staleTime: 60_000 },
  });
  const run = useRunDwcDiagnostic({ request, mutation: { retry: false } });
  if (access.isError) return (
    <div role="alert" className="rounded-lg border border-border p-3 text-xs text-muted-foreground">
      Could not check operator diagnostic access.
      <button type="button" className="ml-2 underline" onClick={() => access.refetch()}>Retry</button>
    </div>
  );
  if (!access.data?.allowed) return null;
  const result = run.data;
  const error = run.error as { data?: { error?: string }; message?: string } | null;
  function download() {
    if (!result) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `NTAW-DEV-connectivity-${result.id}.json`;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <div className="rounded-lg border border-border bg-background/40 p-4 space-y-3" data-testid="dwc-diagnostic-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Operator DEV connectivity check</h3>
          <p className="mt-1 text-xs text-muted-foreground max-w-xl">
            Uses project DEV credentials to check the fixed outbound IP and read 100 stock records.
            No saved merchant connection is used. No Shopify calls or inventory writes.
          </p>
        </div>
        <button type="button" disabled={run.isPending} onClick={() => run.mutate()}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground disabled:opacity-50"
          data-testid="button-dwc-diagnostic">
          {run.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {run.isPending ? 'Checking DEV connectivity…' : 'Run DEV connectivity check'}
        </button>
      </div>
      {run.isPending && <p role="status" className="text-xs text-muted-foreground">Checking through the fixed-IP proxy. This can take up to one minute.</p>}
      {run.isError && <p role="alert" className="text-xs text-red-500">{error?.data?.error ?? error?.message ?? 'Connectivity check failed. Try again.'}</p>}
      {result && !run.isPending && (
        <div className="space-y-3" data-testid="dwc-diagnostic-result" aria-live="polite">
          <p className={`text-sm font-medium ${result.acceptanceReady ? 'text-emerald-600' : result.success ? 'text-amber-600' : 'text-red-500'}`}>
            {result.acceptanceReady ? 'Published-host connectivity check passed' : result.success ? 'DEV connectivity passed — not a published-runtime test' : 'Connectivity check did not pass'}
          </p>
          {result.error && <p role="alert" className="text-xs text-red-500">{result.error}</p>}
          <dl className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
            <div><dt className="text-muted-foreground">Checked</dt><dd>{formatDate(result.checkedAt)}</dd></div>
            <div><dt className="text-muted-foreground">Application host</dt><dd className="break-all">{result.requestHost}</dd></div>
            <div><dt className="text-muted-foreground">Published runtime</dt><dd>{result.publishedRuntime ? 'Yes' : 'No — publish and run again on the live app'}</dd></div>
            <div><dt className="text-muted-foreground">Observed outbound IP</dt><dd>{result.observedIp ?? 'Not verified'}</dd></div>
            <div><dt className="text-muted-foreground">IP / supplier checks</dt><dd>{result.networkStatus} / {result.supplierStatus}</dd></div>
            <div><dt className="text-muted-foreground">Records / duration</dt><dd>{result.recordsRead} / {(result.elapsedMs / 1000).toFixed(2)}s</dd></div>
          </dl>
          <p className="text-xs text-muted-foreground">
            Approved addresses: {result.expectedIps.join(', ')}. This run verifies only the observed address.
            This is connectivity evidence, not the separate three-SKU business reconciliation or full sign-off.
          </p>
          {result.sample.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead><tr className="border-b border-border"><th className="py-1 pr-3">Sample SKU</th><th className="py-1 pr-3">Warehouse</th><th className="py-1">Stock</th></tr></thead>
                <tbody>{result.sample.map((row, index) => <tr key={`${row.sku}-${row.warehouseCode}-${index}`}><td className="py-1 pr-3">{row.sku}</td><td className="py-1 pr-3">{row.warehouseCode}</td><td className="py-1">{row.quantity}</td></tr>)}</tbody>
              </table>
            </div>
          )}
          <button type="button" onClick={download} className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs hover:bg-muted" data-testid="button-dwc-diagnostic-download">
            <Download className="h-3.5 w-3.5" /> Download evidence JSON
          </button>
        </div>
      )}
    </div>
  );
}
